/**
 * UnsavedChangesContext
 * Global context for tracking and warning about unsaved changes
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface UnsavedChangesContextType {
    hasUnsavedChanges: boolean;
    setHasUnsavedChanges: (value: boolean) => void;
    checkAndNavigate: (path: string, callback?: () => void) => void;
    showUnsavedChangesModal: boolean;
    setShowUnsavedChangesModal: (value: boolean) => void;
    pendingNavigation: { path: string; callback?: () => void } | null;
    confirmNavigation: () => void;
    cancelNavigation: () => void;
}

const UnsavedChangesContext = createContext<
    UnsavedChangesContextType | undefined
>(undefined);

export const useUnsavedChanges = () => {
    const context = useContext(UnsavedChangesContext);
    if (!context) {
        throw new Error(
            "useUnsavedChanges must be used within an UnsavedChangesProvider"
        );
    }
    return context;
};

interface UnsavedChangesProviderProps {
    children: React.ReactNode;
}

export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({
    children,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
        useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<{
        path: string;
        callback?: () => void;
    } | null>(null);

    const checkAndNavigate = useCallback(
        (path: string, callback?: () => void) => {
            // If we're on the meal plan editor page and have unsaved changes
            if (
                hasUnsavedChanges &&
                location.pathname.includes("/meal-planner/editor")
            ) {
                setPendingNavigation({ path, callback });
                setShowUnsavedChangesModal(true);
            } else {
                // No unsaved changes or not on editor page, navigate immediately
                if (callback) {
                    callback();
                } else {
                    navigate(path);
                }
            }
        },
        [hasUnsavedChanges, location.pathname, navigate]
    );

    const confirmNavigation = useCallback(() => {
        setShowUnsavedChangesModal(false);
        if (pendingNavigation) {
            if (pendingNavigation.callback) {
                pendingNavigation.callback();
            } else {
                navigate(pendingNavigation.path);
            }
            setPendingNavigation(null);
        }
        setHasUnsavedChanges(false);
    }, [pendingNavigation, navigate]);

    const cancelNavigation = useCallback(() => {
        setShowUnsavedChangesModal(false);
        setPendingNavigation(null);
    }, []);

    return (
        <UnsavedChangesContext.Provider
            value={{
                hasUnsavedChanges,
                setHasUnsavedChanges,
                checkAndNavigate,
                showUnsavedChangesModal,
                setShowUnsavedChangesModal,
                pendingNavigation,
                confirmNavigation,
                cancelNavigation,
            }}
        >
            {children}
        </UnsavedChangesContext.Provider>
    );
};
